import { useCallback, useRef } from 'react';

import { Text, Grid } from '@simplywallst/ui-core';

import { currencyToSymbol } from '@/utilities/formatting';

import useIsomorphicLayoutEffect from '@/hooks/useIsomorphicLayoutEffect';

import type { Holding } from '@/hooks/portfolio/usePortfolio';

import { isValidNumberInput } from '@/utilities/validation';

import { HoldingMoreOptions } from '../HoldingMoreOptions';
import { HoldingInput } from '../HoldingInput';

import type { UpdateManualHoldingInput } from '../../../hooks/useUpdateManualPortfolio';

import type { PortfolioToUpdate } from '../../../context/ManualPortfolioContext';

import { TableCell } from '../styled';

interface Props {
  initialData?: PortfolioToUpdate;
  holding: Holding;
  draftHolding: UpdateManualHoldingInput;
  onInputSave: ({
    symbol,
    exchangeCode,
    units,
    averagePurchasePrice,
  }: UpdateManualHoldingInput) => void;
  onRemoveDraftHolding: ({
    symbol,
    exchangeCode,
  }: {
    symbol: string;
    exchangeCode: string;
  }) => void;
}

export const HoldingInputRow = ({
  initialData,
  holding,
  onInputSave,
  draftHolding,
  onRemoveDraftHolding,
}: Props) => {
  const unitsInput = useRef<HTMLInputElement>(null);
  const avgPriceInput = useRef<HTMLInputElement>(null);

  useIsomorphicLayoutEffect(() => {
    if (!initialData || !unitsInput.current || !avgPriceInput.current) return;

    const { holdingId, holdingFieldToUpdate } = initialData;

    if (holding.id !== holdingId) return;

    if (holdingFieldToUpdate === 'units') {
      unitsInput.current.focus();
    }

    if (holdingFieldToUpdate === 'averagePrice') {
      avgPriceInput.current.focus();
    }
  }, [holding.id, initialData]);

  const companyCurrencySymbol: string | undefined =
    currencyToSymbol[
      holding.company.data.currencyInfo.trading_item_currency_iso
    ]?.currency.symbol;

  const isHoldingComplete =
    holding.units !== 0 && holding.averagePurchasePrice !== null;

  const handleUnitsSave = useCallback(
    (value: string) => {
      if (!isValidNumberInput(value) && value !== '') return;

      onInputSave({
        symbol: draftHolding.symbol,
        exchangeCode: draftHolding.exchangeCode,
        units: value.replace(',', '.'),
        averagePurchasePrice: draftHolding.averagePurchasePrice,
      });
    },
    [
      draftHolding.averagePurchasePrice,
      draftHolding.exchangeCode,
      draftHolding.symbol,
      onInputSave,
    ]
  );

  const handleAvgPriceSave = useCallback(
    (value: string) => {
      if (!isValidNumberInput(value) && value !== '') return;

      onInputSave({
        symbol: draftHolding.symbol,
        exchangeCode: draftHolding.exchangeCode,
        units: draftHolding.units,
        averagePurchasePrice: value.replace(',', '.'),
      });
    },
    [
      draftHolding.exchangeCode,
      draftHolding.symbol,
      draftHolding.units,
      onInputSave,
    ]
  );

  const handleRemoveDraftHolding = useCallback(() => {
    onRemoveDraftHolding({
      symbol: draftHolding.symbol,
      exchangeCode: draftHolding.exchangeCode,
    });
  }, [draftHolding.exchangeCode, draftHolding.symbol, onRemoveDraftHolding]);

  return (
    <tr data-cy-id={`edit-holdings-table-row-${holding.id}`}>
      <TableCell
        isHoldingComplete={isHoldingComplete}
        data-cy-id="company-column"
      >
        <Text typography="xSmall" color="whiteRGBA" opacity={0.7}>
          {holding.company.uniqueSymbol}
        </Text>
        <Text typography="xSmall" isTruncated>
          {holding.company.name}
        </Text>
      </TableCell>
      <TableCell data-cy-id="holding-units">
        <HoldingInput
          ref={unitsInput}
          name="holding-input-units"
          value={draftHolding.units}
          onChange={handleUnitsSave}
        />
      </TableCell>
      <TableCell data-cy-id="holding-average-price">
        <Grid
          templateColumns="1fr max-content"
          justifyContent="space-between"
          alignItems="center"
        >
          <HoldingInput
            ref={avgPriceInput}
            name="holding-input-average-price"
            prefix={companyCurrencySymbol}
            value={draftHolding.averagePurchasePrice}
            onChange={handleAvgPriceSave}
          />
          <HoldingMoreOptions
            onRemove={handleRemoveDraftHolding}
            holdingId={holding.id}
          />
        </Grid>
      </TableCell>
    </tr>
  );
};
